// src/features/scheduling/components/TestSelection.tsx
import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

import { Search, FlaskConical, Check, AlertCircle, ChevronDown, X, ChevronRight } from "lucide-react";
import type { TestSelection as TestSelectionType } from "../types";

interface TestSelectionProps {
  tests: TestSelectionType[];
  isLoading: boolean;
  selectedTests: TestSelectionType[];
  onSelectTest: (test: TestSelectionType) => void;
  onDeselectTest: (testId: number) => void;
  onSelectAll: (tests: TestSelectionType[]) => void;
  personHasTests?: boolean;
}

export default function TestSelection({
  tests,
  isLoading,
  selectedTests,
  onSelectTest,
  onDeselectTest,
  onSelectAll: _onSelectAll,
  personHasTests = false,
}: TestSelectionProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [componentSearchQuery, setComponentSearchQuery] = useState("");
  const [selectedComponents, setSelectedComponents] = useState<string[]>([]);
  const [isComponentPopoverOpen, setIsComponentPopoverOpen] = useState(false);

  const allComponents = useMemo(() => {
    const components = new Map<string, string>();
    tests.forEach((test) => {
      (test.testComponents || []).forEach((component) => {
        const normalized = component.trim();
        if (normalized) {
          const key = normalized.toLowerCase();
          if (!components.has(key)) {
            components.set(key, normalized);
          }
        }
      });
    });
    return Array.from(components.values()).sort((a, b) => a.localeCompare(b));
  }, [tests]);

  const filteredComponentOptions = useMemo(() => {
    const query = componentSearchQuery.trim().toLowerCase();
    if (!query) return allComponents;
    return allComponents.filter((component) =>
      component.toLowerCase().includes(query)
    );
  }, [allComponents, componentSearchQuery]);

  const filteredTests = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    const hasTextQuery = Boolean(query);
    const hasComponentFilter = selectedComponents.length > 0;

    return tests.filter((test) => {
      const normalizedTestComponents = (test.testComponents || []).map((component) =>
        component.trim().toLowerCase()
      );

      const matchesText = !hasTextQuery
        ? true
        : Boolean(
            test.testName.toLowerCase().includes(query) ||
              test.testCategory.toLowerCase().includes(query) ||
              test.testDescription?.toLowerCase().includes(query) ||
              normalizedTestComponents.some((component) =>
                component.includes(query)
              )
          );

      const matchesComponent = !hasComponentFilter
        ? true
        : selectedComponents.every((component) => {
            const normalizedSelected = component.trim().toLowerCase();
            return normalizedTestComponents.includes(normalizedSelected);
          }
          );

      return matchesText && matchesComponent;
    });
  }, [tests, searchQuery, selectedComponents]);

  const isSelected = (testId: number) => 
    selectedTests.some((t) => t.id === testId);

  const handleToggleTest = (test: TestSelectionType) => {
    if (isSelected(test.id)) {
      onDeselectTest(test.id);
    } else {
      onSelectTest(test);
    }
  };

  // Group tests by category
  const testsByCategory = useMemo(() => {
    const grouped: Record<string, TestSelectionType[]> = {};
    filteredTests.forEach((test) => {
      if (!grouped[test.testCategory]) {
        grouped[test.testCategory] = [];
      }
      grouped[test.testCategory].push(test);
    });
    return grouped;
  }, [filteredTests]);

  const categoryColors: Record<string, string> = {
    DRUG: "bg-purple-100 text-purple-800 border-purple-200",
    BLOOD: "bg-red-100 text-red-800 border-red-200",
    URINE: "bg-yellow-100 text-yellow-800 border-yellow-200",
    DNA: "bg-blue-100 text-blue-800 border-blue-200",
    GENERAL: "bg-gray-100 text-gray-800 border-gray-200",
    DEFAULT: "bg-slate-100 text-slate-800 border-slate-200",
  };

  const getCategoryColor = (category: string) => {
    return categoryColors[category.toUpperCase()] || categoryColors.DEFAULT;
  };

  return (
    <Card className="border-2 border-slate-200 dark:border-slate-700 overflow-hidden">
      <CardHeader className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
              <FlaskConical className="w-5 h-5" />
            </div>
            <div>
              <CardTitle className="text-lg">Select Tests</CardTitle>
              <p className="text-sm text-emerald-100 mt-0.5">
                Choose tests for the appointment
              </p>
            </div>
          </div>
          {selectedTests.length > 0 && (
            <Badge className="bg-white/20 text-white border-0 backdrop-blur-sm text-sm px-3 py-1">
              {selectedTests.length} selected
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="p-4 space-y-4">
        {/* Person has pre-selected tests notification */}
        {personHasTests && (
          <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <AlertCircle className="w-5 h-5 text-blue-600" />
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Tests have been pre-selected based on the visitor's profile
            </p>
          </div>
        )}

        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search tests by name, category, or component..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-11 border-slate-300 dark:border-slate-600 focus:border-emerald-500"
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <Popover open={isComponentPopoverOpen} onOpenChange={setIsComponentPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className="justify-between sm:min-w-[320px]"
              >
                <span className="truncate">
                  {selectedComponents.length > 0
                    ? `${selectedComponents.length} component${selectedComponents.length > 1 ? "s" : ""} selected`
                    : "Filter by component (Drug test only)"}
                </span>
                <ChevronDown className="w-4 h-4 ml-2 text-slate-500" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[340px] p-3 space-y-2" align="start">
              <Input
                value={componentSearchQuery}
                onChange={(e) => setComponentSearchQuery(e.target.value)}
                placeholder="Search components..."
                className="h-9"
              />
              <div className="max-h-52 overflow-y-auto border rounded-md">
                {filteredComponentOptions.length === 0 ? (
                  <p className="px-3 py-2 text-sm text-slate-500">No components found</p>
                ) : (
                    filteredComponentOptions.map((component) => (
                      <div
                        key={component}
                        onClick={() => {
                          setSelectedComponents((prev) =>
                            prev.includes(component)
                              ? prev.filter((item) => item !== component)
                              : [...prev, component]
                          );
                        }}
                        className="w-full text-left px-3 py-2.5 text-sm hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-between cursor-pointer rounded-sm group transition-colors"
                      >
                        <span className="truncate pr-2 group-hover:text-emerald-600 transition-colors">{component}</span>
                        {selectedComponents.includes(component) && (
                          <div className="bg-emerald-600 rounded-full p-0.5">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                    ))
                )}
              </div>
            </PopoverContent>
          </Popover>

          {selectedComponents.length > 0 && (
            <Button
              type="button"
              variant="ghost"
              className="sm:w-auto justify-start"
              onClick={() => {
                setSelectedComponents([]);
                setComponentSearchQuery("");
              }}
            >
              <X className="w-4 h-4 mr-1" />
              Clear
            </Button>
          )}
        </div>
        {selectedComponents.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {selectedComponents.map((component) => (
              <Badge
                key={component}
                variant="secondary"
                className="cursor-pointer"
                onClick={() =>
                  setSelectedComponents((prev) => prev.filter((item) => item !== component))
                }
              >
                {component}
                <span className="ml-1">×</span>
              </Badge>
            ))}
          </div>
        )}

        {/* Selected Tests Summary */}
        {selectedTests.length > 0 && (
          <div className="flex flex-wrap gap-2 p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg">
            {selectedTests.map((test) => (
              <Badge
                key={test.id}
                className="bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-200 cursor-pointer transition-colors"
                onClick={() => onDeselectTest(test.id)}
              >
                {test.testName}
                <span className="ml-1 text-emerald-600">×</span>
              </Badge>
            ))}
          </div>
        )}

        {/* Test List by Category */}
        <div className="max-h-80 overflow-y-auto space-y-4">
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="h-16 bg-slate-100 dark:bg-slate-800 animate-pulse rounded-lg"
                />
              ))}
            </div>
          ) : Object.keys(testsByCategory).length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <FlaskConical className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">No tests found</p>
              <p className="text-sm">Try adjusting your search</p>
            </div>
          ) : (
            Object.entries(testsByCategory).map(([category, categoryTests]) => (
              <div key={category} className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className={`${getCategoryColor(category)} font-medium`}
                  >
                    {category}
                  </Badge>
                  <span className="text-sm text-slate-500">
                    ({categoryTests.length} tests)
                  </span>
                </div>
                <div className="grid gap-2">
                  {categoryTests.map((test) => (
                    <div
                      key={test.id}
                      onClick={() => handleToggleTest(test)}
                      className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all duration-300 cursor-pointer group ${
                        isSelected(test.id)
                          ? "border-emerald-500 bg-emerald-50/50 dark:bg-emerald-900/20 ring-1 ring-emerald-500 shadow-md"
                          : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-emerald-400 hover:bg-slate-50/80 dark:hover:bg-slate-800/80 hover:shadow-lg"
                      }`}
                    >
                      <div
                        className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all duration-200 shadow-sm ${
                          isSelected(test.id)
                            ? "bg-emerald-500 border-emerald-500 scale-110"
                            : "border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 group-hover:border-emerald-300"
                        }`}
                      >
                        {isSelected(test.id) && (
                          <Check className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-bold text-base truncate ${
                          isSelected(test.id) ? "text-emerald-700 dark:text-emerald-300" : "text-slate-900 dark:text-slate-100"
                        }`}>
                          {test.testName}
                        </p>
                        {test.testDescription && (
                          <p className="text-sm text-slate-500 dark:text-slate-400 truncate mt-0.5">
                            {test.testDescription}
                          </p>
                        )}
                        {!!test.testComponents?.length && (
                          <div className="flex items-center gap-1.5 mt-2 overflow-hidden">
                             <div className="flex flex-wrap gap-1.5">
                                {test.testComponents.map((comp, idx) => (
                                  <span key={idx} className="text-[10px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 px-1.5 py-0.5 rounded uppercase tracking-tighter">
                                    {comp}
                                  </span>
                                ))}
                                {/* {test.testComponents.length > 3 && (
                                  <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">
                                    +{test.testComponents.length - 3} more
                                  </span>
                                )} */}
                             </div>
                          </div>
                        )}
                      </div>
                      {isSelected(test.id) ? (
                        <div className="bg-emerald-100 text-emerald-700 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded shadow-sm border border-emerald-200">
                          Selected
                        </div>
                      ) : (
                        <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-emerald-400 transition-colors" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}
